import type { OrderResource } from "../../order";
import type { OwnerProfileResource } from "../../owner-profile";
import type { OwnerResource } from "../../owner";
import type { AddressResource } from "../../../schemas";
import type { CustomerResource } from "../../customer";
import type { CurrencyResource } from "../../currency";
import type { PlaceOfSupplyRuleEnum, SupplyTypeEnum } from "../../product";
import type {
  EmailContactResource,
  PhoneContactResource,
} from "../../contact/schemas";
import type { PositionResource } from "../../accounting-posting";
import type {
  NotificationMessageResource,
  PaymentMethodResource,
  SupplierResource,
} from "@exonn/js-sdk";
import type { DocumentPartyTypeEnum } from "../../../../../../src/admin/modules/document/forms/document/constants.ts";

export type DocumentStoreRequest = {
  document_type: string;
  owner_profile_id: string;
  customer_id: string;
  currency_id: string;
  order_id: string;
  is_phone_printed: boolean;
  is_fax_printed: boolean;
  is_email_printed: boolean;
  text_start?: string;
  text_end?: string;
  calculable_document: {
    is_net_mode: boolean;
  };
  payable_document: {
    payment_method_id: string;
    payment_terms?: number | null;
  };
  deliverable_document?: {
    delivery_method_id?: string;
  };
};

export type DocumentUpdateRequest = {
  document_type: string;
  owner_profile_id: string;
  customer_id: string;
  currency_id: string;
  order_id: string;
  is_phone_printed: boolean;
  is_fax_printed: boolean;
  is_email_printed: boolean;
  text_start?: string;
  text_end?: string;
  calculable_document: {
    is_net_mode: boolean;
  };
  payable_document: {
    payment_method_id: string;
    payment_terms?: number | null;
  };
  deliverable_document?: {
    delivery_method_id?: string;
  };
  document_positions?: DocumentPosition[];
};

export interface RecurringDocumentScheduleResource {
  id: string;
  source_document_id: string;
  target_document_type: string;
  interval: RecurringScheduleIntervalEnum;
  status: RecurringScheduleStatusEnum;
  start_date: string;
  end_date: string;
  next_run_date: string;
  last_run_date: string;
  created_at: string;
  updated_at: string;
}

export interface PayableDocumentResource {
  id: string;
  payment_method?: PaymentMethodResource;
  payment_method_id: string;
  payment_terms?: number | null;
  reminder_date: string | null;
  reminder_level: string;
  invoice_id: string | null;
  document_id: string;
  document?: DocumentResource;
  invoice?: DocumentResource | null;
}

export interface DocumentResource {
  id: string;
  oxid: string | null;
  document_type: DocumentTypes;
  group_number: string;
  process_number: string;
  document_date: string;
  document_number: string;
  owner_profile_id: string;
  customer_id: string;
  supplier_id?: string;
  party_type: DocumentPartyTypeEnum;
  currency_id: string;
  order_id: string;
  billing_address_id: string;
  delivery_address_id: string;
  order?: OrderResource;
  owner_profile: OwnerProfileResource;
  owner?: OwnerResource;
  billing_address?: AddressResource;
  delivery_address?: AddressResource;
  customer?: CustomerResource;
  supplier?: SupplierResource;
  currency?: CurrencyResource;
  document_serial_number?: any;
  recurring_schedule?: RecurringDocumentScheduleResource;
  recurring_run?: RecurringDocumentRunResource;
  recurring_runs?: RecurringDocumentRunResource[];
  document_vats?: {
    net_sum: number;
    vat_percent: number;
    vat_sum: number;
    position_type?: string;
  }[];
  calculable_document: {
    is_net_mode: boolean;
  };
  payable_document: PayableDocumentResource;
  payable_documents?: PayableDocumentResource[];
  deliverable_document: {
    delivery_method_id: string;
  };
  document_total_gross?: number;
  document_positions_sum: number;
  is_phone_printed: boolean;
  is_fax_printed: boolean;
  is_email_printed: boolean;
  text_start: string;
  text_end: string;
  created_at: string;
  document_positions?: DocumentPosition[];
  document_additional_services: DocumentAdditionalServiceResource[];
  billing_phones?: PhoneContactResource[];
  billing_email?: EmailContactResource;
  delivery_phones?: PhoneContactResource[];
  delivery_email?: EmailContactResource;
  accounting_posting_positions?: PositionResource[];
  notification_messages?: NotificationMessageResource[];
}

export interface RecurringDocumentRunResource {
  id: string;
  source_document_id: string;
  generated_document_id: string;
  source_document?: DocumentResource;
  generated_document?: DocumentResource;
  created_at: string;
  error: string;
  status: string;
  scheduled_run_date: string;
  schedule_id: string;
  schedule?: RecurringDocumentScheduleResource;
}

export enum RecurringScheduleStatusEnum {
  Active = "active",
  Stopped = "stopped",
}

export enum RecurringScheduleIntervalEnum {
  Weekly = "weekly",
  Monthly = "monthly",
  Quarterly = "quarterly",
  HalfYearly = "half-yearly",
  Yearly = "yearly",
}

export enum DocumentTypes {
  Invoice = "invoice", // Rechnung
  Credit = "credit", // Gutschrift
  Offer = "offer", // Angebot
  Listing = "listing", // Aufstellung
  Estimate = "estimate", // Kostenvoranschlag
  ProformaInvoice = "proforma-invoice", // Proforma-Rechnung ??????
  DeliveryNote = "delivery-note", // Lieferschein
  // DeliveryNoteCancellation = "delivery-note-cancellation", // Lieferschein-Storno ????
  PaymentReminder = "payment-reminder", // Zahlungserinnerung
  Reminder = "reminder", // Mahnung
  OrderConfirmation = "order-confirmation", // Auftragsbestätigung
  //Calculation = "calculation", // Zerrechnung
  Letter = "letter", // Brief
  // PaymentAdvice = "payment-advice", // Zahlungsaufforderung
  // SupplierOrder = "supplier-order", // VendorOrder
}

export interface DocumentTemplateResource {
  id: string;
  name: string;
  is_phone_printed: boolean;
  document_type: DocumentTypes;
  is_fax_printed: boolean;
  is_email_printed: boolean;
  text_start: string;
  text_end: string;
  created_at: string;
  ownerProfiles?: OwnerProfileResource[];
  owner_profile_ids?: string[];
}

export type DocumentAdditionalServiceResource = {
  id: string;
  position_id?: string;
  position_type: string;
  vat_percent: number | null;
  title: string;
  input_value: number | null;
  is_value_percent: boolean;
  total_price: string;
};

export type DocumentPosition = {
  id?: string;
  position_id?: string;
  total_price?: number;
  product_number: string;
  product_title: string;
  quantity: number;
  unit_price: number;
  vat_percent?: string | null;
  discount?: number;
  is_discount_percent?: boolean;
  serial_number: string;
  /** Historischer Snapshot (P1-3), setzt nur der Server; null = nicht festgelegt. */
  supply_type?: SupplyTypeEnum | null;
  /** null = nicht geprüft. */
  place_of_supply_rule?: PlaceOfSupplyRuleEnum | null;
  supply_classification_source?: "order_variant" | "source_document" | null;
  /** Bezug einer Gutschriftposition auf die Rechnungsposition. */
  source_document_position_id?: string | null;
};

export type CalculateRequest = {
  document_type: string;
  document_positions: DocumentPosition[];
  document_additional_services: DocumentAdditionalServiceResource[];
  calculable_document: {
    is_net_mode: boolean;
  };
};

export type DocumentCalculateResource = {
  calculable_document: {
    document_total_net: number;
  };
  document_additional_services: DocumentAdditionalServiceResource[];
  document_positions_vats: any;
  document_positions: DocumentPosition[];
  document_positions_sum: number;
  document_total_gross: number;
  document_total_vats: {
    net_sum: number;
    vat_percent: number;
    vat_sum: number;
  }[];
};

export interface DocumentAddressResource {
  billing_address: {
    salutation: string | null;
    first_name: string | null;
    last_name: string | null;
    company: string | null;
    address_line_1: string | null;
    address_line_2: string | null;
    zip: string | null;
    city: string | null;
    country_id: string | null;
  };
  delivery_address: {
    salutation: string | null;
    first_name: string | null;
    last_name: string | null;
    company: string | null;
    address_line_1: string | null;
    address_line_2: string | null;
    zip: string | null;
    city: string | null;
    country_id: string | null;
  };
  billing_phones: Partial<PhoneContactResource>[];
  billing_email: Partial<EmailContactResource>;
  delivery_phones: Partial<PhoneContactResource>[];
  delivery_email: Partial<EmailContactResource>;
}

export type UpdateDocumentTextsRequestBody = {
  text_start: string;
  text_end: string;
};

export type UpdateDocumentAddressRequestBody = {
  billing_address: {
    salutation: string | null;
    first_name: string | null;
    last_name: string | null;
    company: string;
    address_line_1: string;
    address_line_2: string;
    zip: string;
    city: string;
    country: string;
  };
  delivery_address: {
    salutation: string | null;
    first_name: string;
    last_name: string;
    company: string;
    address_line_1: string;
    address_line_2: string;
    zip: string;
    city: string;
    country: string;
  };
  billing_phones: Partial<PhoneContactResource>[];
  billing_email: Partial<EmailContactResource>;
  delivery_phones: Partial<PhoneContactResource>[];
  delivery_email: Partial<EmailContactResource>;
};
